import { Component, OnInit, EventEmitter, Output, ViewChild } from '@angular/core';
import { round } from '@turf/turf';
import { index } from 'd3-array';
import { start } from 'repl';
import { forkJoin, Observable } from 'rxjs';
import { HistoryService } from 'src/app/history-analysis/services/history/history.service';
import { AlertMatrixComponent } from '../alert-matrix/alert-matrix.component';
import { Alerta } from '../models/alerta';
import { Tempo } from '../models/tempo';
import { CSVAux } from '../services/csv-aux/csv-aux';
import { MathAux } from '../services/math-aux/math-aux';
import { ServiceService } from '../services/service.service';
import { TimespanService } from '../services/timespan/timespan.service';
import { TimeFormatter } from '../utils/time-formatter';

/**
 * Este componente mostra uma tabela de resumo dos alertas e avisos cujos intervalos 
 * de tempo abrangem o tempo sendo consultado. As colunas da tabela são as intensidades 
 * de chuva identificadas na mensagem, a data de início, a data de fim, e o tipo 
 * (alerta ou aviso). Também é possível ver uma mensagem na íntegra.
 */
@Component({
  selector: 'app-alert-list',
  templateUrl: './alert-list.component.html',
  styleUrls: ['./alert-list.component.css']
})
export class AlertListComponent implements OnInit {

  @Output()
  tableClosed: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  searchDate: EventEmitter<any> = new EventEmitter<any>();

  @Output() dragDisabled = new EventEmitter<boolean>();

  @Output() forceUpdateTempo = new EventEmitter<any>();


  StationsByAlert$: Observable<any[]>[];

  displayedColumns :string[] = [];

  RT : boolean = false;

  alertColumns = [
    { 'name': 'intensidades_previstas', 'text': 'Intensidades', 'class': 'width-6' },
    // { 'name': 'mensagem', 'text': 'Mensagem', 'class': 'width-20', },
    { 'name': 'horario_inicial', 'text': 'Data inicial', 'class': 'width-10', },
    { 'name': 'horario_final', 'text': 'Data final', 'class': 'width-10', },
    // { 'name': 'motivo', 'text': 'Motivo', 'class': 'width-6', },
    { 'name': 'classificacao', 'text': 'Classificação', 'class': 'width-6', },
  ]

  visible: boolean = false;
  showTable: boolean = true;
  alerts : Alerta[] = [];
  alertStations : any[] = []
  tempo : Tempo;

  selecionado: Alerta;
  showDetails: boolean = false;
  showAlertMatrix: boolean = false;

  private discreteColors: {[key: string]: string} = {
    'fraca':'#339944', 
    'moderada':'#FFCC00', 
    'forte': '#FF8822', 
    'muito forte':'#DD2233'
  };

  @ViewChild(AlertMatrixComponent, {static: false}) alertMatrix: AlertMatrixComponent;

  constructor(private service: ServiceService, private timespanService: TimespanService, private hService: HistoryService) {}

  ngOnInit(): void {
    this.displayedColumns = this.alertColumns.map(col => col.name);
  }

  /**
   * Alerta entre exibir e esconder o componente.
   */
  changeView() {
    this.visible = ! this.visible;
    if (this.visible == false) {
      this.tableClosed.emit(false);
    }
  }

  /**
   * Esconde o componente.
   */
  close() {
    this.visible = false;
    this.tableClosed.emit(false);
  }


  /**
   * Atualiza os dados da tabela de alertas e avisos.
   * 
   * @param alerts alertas e avisos buscados. 
   * @param RT avisa a alertlist se o projeto está em tempo real
   */
  update(alerts: Alerta[],RT:boolean,tempo:Tempo) {
    this.alerts = alerts;
    this.tempo = tempo
    this.alertStations = []
    this.StationsByAlert$ = []
    this.alerts.forEach((val)=>{
      let endId,startId;
      startId = parseInt(val.id_tempo_inicial);
      if(val.id_tempo_final == "0"){
        let startDate = this.timespanService.formattedDateToDate(val.horario_inicial)
        let nowDate = new Date();
        endId = startId + round(((nowDate.getTime()-startDate.getTime())/60000));
        this.RT = true
      } else {
        endId = (parseInt(val.id_tempo_final)+60); 
        this.RT = false;
      }
      startId -= 60;
      
      this.StationsByAlert$.push(this.service.getRainDataByIntensities(startId.toString(),endId.toString(),`true`,`true`,
      `true`,`true`))
    });
    
    if(this.alerts.length > 0)
    forkJoin(this.StationsByAlert$).subscribe((req)=>{
      req.forEach( (val,index)=>{
        this.alertStations.push(this.removeStationDuplicates(val,this.alerts[index].intensidades_previstas));
      })
      if(this.selecionado && this.showAlertMatrix && this.alerts.filter(val=>val.id_tempo_inicial == this.selecionado.id_tempo_inicial).length > 0){
        this.openAlertMatrix(this.selecionado,this.alerts.indexOf(this.alerts.filter(val=>val.id_tempo_inicial == this.selecionado.id_tempo_inicial)[0]));
      } else {
        this.unselectAlert()
      }
    })
    else this.unselectAlert()
  }

  /**
   * Desmarca o alerta / aviso como selecionado, para sair da visualização da
   * mensagem completa e voltar para a tabela do resumo dos alertas.
   */
  unselectAlert() {
    this.selecionado = null;
    this.showTable = true;
    this.showDetails = false;
    this.showAlertMatrix = false;
    if(this.alertMatrix)
    this.alertMatrix.heatmatrixClass = "alertMatrix-hide";
  }

  /**
   * Marca um alerta / aviso como selecionado, para ver sua mensagem na íntegra.
   * @param alerta 
   */
  selectAlert(alerta: Alerta) {
    this.selecionado = alerta;
    this.showTable = false;
    this.showDetails = true;
    this.showAlertMatrix = false;
  }

  /**
   * Obtém a cor correspondente a uma intensidade de chuva.
   * @param intensity nome da intensidade de chuva.
   * @returns cor associada.
   */
  getColor(intensity: string): string {

    return this.discreteColors[intensity];
  }

  /**
   * Alterna a exibição para a alert matrix.
   * @param element alerta / aviso selecionado para exibir as intensidades de chuva
   * que as estações registraram dentro de seu intervalo de tempo.
   * @param index 
   */
  openAlertMatrix(element: Alerta, index: number) {
    this.selecionado = element;
    this.showTable = false;
    this.showDetails = false;
    this.showAlertMatrix = true;
    this.alertMatrix.alerta = element;
    this.alertMatrix.tempo = this.tempo;
    this.alertMatrix.updateAlertMatrix(this.alertStations[index],this.RT);
  }

  /**
   * Para cada alerta / aviso, obtém as intensidades de chuva citadas na mensagem, 
   * e a lista das estações, cada uma com os arrays das suas respectivas medições .
   * @param stations medições de chuva dentro do intervalo do alerta / aviso.
   * @param intensities intensidades de chuva previstas na mensagem do alerta / aviso.
   * @returns objeto com as intensidades previstas e o array das estações com suas medições.
   */
  removeStationDuplicates(stations: any[], intensities: string[]){
    let newArray = [];
    let i = 0, cont = 1;  
    while(i < stations.length){
      cont = i + 1;
      // nomes das intensidades obtidas, p. ex. "fraca", "moderada", "forte"
      let auxInstensities = intensities.slice();
      // obtém os dados de tempo e chuva da estação atual e adiciona todos os dados da mesma estação no auxArray
      let auxArray = [stations[i]]
      while(cont < stations.length && stations[i].nome_estacao_original == stations[cont].nome_estacao_original){
        auxArray.push(stations[cont])
        cont++;
      }
      // ordena os dados de chuva da estação pelo tempo
      auxArray.sort((a,b) => {return a.id_tempo - b.id_tempo})
      // resumo da estação atual: primeiros dados da chuva fraca, moderada, forte e muito forte
      let auxStation = {values : {fraca: {formattedText: "" , properties: ""},moderada: {formattedText: "" , properties: ""}, forte: {formattedText: "" , properties: ""}, muitoforte: {formattedText: "" , properties: ""}},
      station: stations[i].nome_estacao_original, timespan: "",rawData: [{}]};
      auxArray.forEach( (val)=>{
        if (auxInstensities.includes("fraca") && val.quinzemin > 0 && val.quinzemin < 0.625){
          auxInstensities = auxInstensities.filter(function(item) { return item != "fraca" })
          auxStation.values.fraca.properties = val
          auxStation.values.fraca.formattedText = this.getFormattedText(val)
        }
        if (auxInstensities.includes("moderada") && val.quinzemin >= 0.625 && val.quinzemin < 2.5){
          auxInstensities = auxInstensities.filter(function(item) { return item != "moderada" })
          auxStation.values.moderada.properties = val
          auxStation.values.moderada.formattedText = this.getFormattedText(val)
        }
        if (auxInstensities.includes("forte") && val.quinzemin >= 2.5 && val.quinzemin < 12.5){
          auxInstensities = auxInstensities.filter(function(item) { return item != "forte" })
          auxStation.values.forte.properties = val
          auxStation.values.forte.formattedText = this.getFormattedText(val)
        }
        if (auxInstensities.includes("muito forte") && val.quinzemin >= 12.5){
          auxInstensities = auxInstensities.filter(function(item) { return item != "muito forte" })
          auxStation.values.muitoforte.properties = val
          auxStation.values.muitoforte.formattedText = this.getFormattedText(val)
        }
      });
      let timespan = this.timespanService.createFormattedTimespan(auxArray);
      auxStation.timespan = timespan;
      auxStation.rawData = auxArray;
      // adiciona o resumo de dados da estação
      newArray.push(auxStation);
      // o índice pula todos os objetos referentes à mesma estação
      i += auxArray.length
    }
    return {intensities: intensities, stations: newArray}
  }

  /**
   * Formata um objeto com valores de data em uma string.
   * @param val objeto de registro de dim_tempo
   * @returns string no formato hh:mm dd/MM/YYYY
   */
  getFormattedText(val : Tempo):string{
    let formattedText,formatedmin,formatedhour,formatedday,formatedmonth

    if (val.minuto < 10){formatedmin = "0"+val.minuto} else {formatedmin = val.minuto}
    if (val.hora < 10){formatedhour = "0"+val.hora} else {formatedhour = val.hora}
    if (val.dia < 10){formatedday = "0"+val.dia} else {formatedday = val.dia}
    if (val.mes < 10){formatedmonth = "0"+val.mes} else {formatedmonth = val.mes}
    formattedText = `${formatedhour}:${formatedmin} - ${formatedday}/${formatedmonth}/${val.ano}`

    return formattedText
  }


  /**
   * Gera o arquivo csv da tabela dos alertas / avisos.
   */
  generateCSV(){
    if (this.alerts.length > 0){
      let filename = `alerts-${TimeFormatter.toLocaleString(this.tempo)}.csv`
      CSVAux.exportArrToCsv(filename,this.prepareAlertListCSV());
    }
  }

  /**
   * Gera o array das colunas a serem adicionadas no arquivo csv a ser exportado.
   * @returns 
   */
  prepareAlertListCSV(){
   let array = [['Mensagem','Data Inicial','Hora Inicial','Data Final','Hora Final','Motivo','Classificação','Intensidades Previstas','Eficácia']];
   let line
   this.alerts.forEach((val,index)=>{
    line = [val.mensagem,val.horario_inicial.slice(0,10),val.horario_inicial.slice(11),val.horario_final.slice(0,10),val.horario_final.slice(11),
    val.motivo,val.classificacao,val.intensidades_previstas.join(",")]
    if(this.alertStations[index].stations.length > 0) line.push('eficaz')
    else line.push('ineficaz')
    array.push(line)
   })
   

   return array
  }

  /**
   * 
   * @param event 
   */
  headerMouseDown(event: any) {
    this.dragDisabled.emit(false)
  }

  /**
   * 
   * @param event 
   */
  headerMouseUp(event: any) {
    this.dragDisabled.emit(true);
  }

  /**
   * Indica se houve chuva nas intensidades previstas por um dado alerta. 
   * @param j índice do alerta no array de alertas.
   * @returns true se a previsão da intensidade de chuva está correta; false, caso contrário.
   */
  getAlertCheck(j:number){
    let aux = false;
    if(this.alerts.length > 0 && this.alertStations.length > 0  ){
      this.alerts[j].intensidades_previstas.forEach((intensidade)=>{
        if(this.alertStations[j].stations.filter((val:any)=>val.values[intensidade].formattedText != "" ).length > 0){
          aux = true;
        }
      })
      return aux;
    }else return false;
  }

}
